import { cn } from '@/lib/utils';
import { buildDiffCardClass, buildDiffPageClass } from './build-diff-layout';

const SkeletonBar = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'animate-pulse rounded-md bg-[var(--inv-surface-muted)]',
      className,
    )}
  />
);

const BuildAnalysisSkeleton = () => (
  <div
    className={cn(buildDiffPageClass, 'animate-pulse')}
    aria-busy="true"
    aria-label="Loading build analysis">
    {/* Page header */}
    <header className="mb-5 max-w-[42rem]">
      <SkeletonBar className="mb-3 h-2.5 w-24" />
      <SkeletonBar className="mb-2 h-8 w-[min(100%,18rem)]" />
      <SkeletonBar className="h-4 w-[min(100%,28rem)]" />
    </header>

    {/* Project context */}
    <SkeletonBar className="mb-4 h-11 w-full rounded-xl" />

    <div className="mb-5 grid grid-cols-1 items-start gap-4">
      {/* Compare builds card */}
      <section className={buildDiffCardClass}>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <SkeletonBar className="mb-2 h-5 w-36" />
            <SkeletonBar className="h-3.5 w-[min(100%,20rem)]" />
          </div>
          <SkeletonBar className="h-9 w-[11rem] shrink-0 rounded-[10px]" />
        </div>

        <div className="hidden grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-x-5 min-[769px]:grid">
          <div className="flex flex-col gap-2">
            <SkeletonBar className="h-3 w-20" />
            <SkeletonBar className="h-10 w-full rounded-[10px]" />
          </div>
          <SkeletonBar className="mx-1 h-[1.35rem] w-[1.35rem] shrink-0 rounded-full" />
          <div className="flex flex-col gap-2">
            <SkeletonBar className="h-3 w-16" />
            <SkeletonBar className="h-10 w-full rounded-[10px]" />
          </div>
        </div>

        <div className="flex flex-col gap-4 min-[769px]:hidden">
          <div className="flex flex-col gap-2">
            <SkeletonBar className="h-3 w-20" />
            <SkeletonBar className="h-10 w-full rounded-[10px]" />
          </div>
          <SkeletonBar className="mx-auto h-[1.35rem] w-[1.35rem] rounded-full" />
          <div className="flex flex-col gap-2">
            <SkeletonBar className="h-3 w-16" />
            <SkeletonBar className="h-10 w-full rounded-[10px]" />
          </div>
        </div>

        <div className="mt-5 flex justify-end border-t border-[var(--inv-border-soft)] pt-4">
          <SkeletonBar className="h-10 w-52 rounded-[10px]" />
        </div>
      </section>

      {/* Activity timeline card */}
      <section
        className={cn(
          buildDiffCardClass,
          'flex max-h-[min(22rem,38vh)] min-w-0 flex-col',
        )}>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <SkeletonBar className="mb-2 h-5 w-40" />
            <SkeletonBar className="h-3.5 w-44" />
          </div>
          <SkeletonBar className="h-6 w-24 rounded-full" />
        </div>

        <div className="relative flex flex-1 flex-col gap-3 pr-1">
          <div
            className="absolute bottom-0 left-[18px] top-0 w-0.5 bg-[var(--inv-border-soft)]"
            aria-hidden
          />
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="relative flex gap-3.5">
              <SkeletonBar className="z-[1] h-9 w-9 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 rounded-[10px] border border-solid border-[var(--inv-border-soft)] border-l-[3px] border-l-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-3.5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <SkeletonBar className="mb-2 h-4 w-28" />
                    <SkeletonBar className="h-3 w-20" />
                  </div>
                  <SkeletonBar className="h-3 w-16 shrink-0" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  </div>
);

export default BuildAnalysisSkeleton;
